import type { EntryValueViewT } from "@patient-watch/types";

/**
 * Carries the validator's own email across the new tab opened by "Open form to review".
 * The public completion route treats this as a screen-review session and shows the
 * in-app thank-you screen instead of the email gate or the results page.
 *
 * localStorage, not sessionStorage or a search param: verified empirically that a
 * `target="_blank"` link does NOT get a copy of the opener's sessionStorage in practice,
 * and a search param on the entry URL gets dropped by the first internal `navigate()` call
 * in the multi-step form-taking flow. localStorage is shared across same-origin tabs
 * regardless of navigation, so it needs no changes to that flow.
 *
 * Read-once-and-clear keeps this scoped to the validator's own click: nothing else in the
 * app ever sets this key, so the ordinary "Test form" builder action and real public leads
 * are completely unaffected.
 */
const VALIDATOR_AUTOSUBMIT_EMAIL_KEY = "pw_validator_autosubmit_email";
const VALIDATOR_FORM_REVIEW_KEY = "pw_validator_form_review";

export interface ValidatorFormReview {
    validationId: string;
    formId: string;
    email?: string | null;
}

/** Remember which validation to return to after this form is finished. */
export function markValidatorFormReview(validationId: string, formId: string, email?: string | null): void {
    try {
        localStorage.setItem(VALIDATOR_FORM_REVIEW_KEY, JSON.stringify({ validationId, formId, email: email ?? null }));
    } catch {
        /* ignore */
    }
}

export function readValidatorFormReview(): ValidatorFormReview | null {
    try {
        const raw = localStorage.getItem(VALIDATOR_FORM_REVIEW_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ValidatorFormReview;
        if (!parsed?.validationId || !parsed?.formId) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearValidatorFormReview(): void {
    try {
        localStorage.removeItem(VALIDATOR_FORM_REVIEW_KEY);
    } catch {
        /* ignore */
    }
}

export function validationReviewResultsKey(validationId: string) {
    return `pw_validation_review_results:${validationId}`;
}

/** Answers saved by `submitValidatorFormReview` in this tab, or null when there are none to show. */
export function readValidationReviewResults(validationId: string): EntryValueViewT[] | null {
    try {
        const raw = sessionStorage.getItem(validationReviewResultsKey(validationId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { entryValues?: EntryValueViewT[] };
        return parsed.entryValues ?? [];
    } catch {
        return null;
    }
}

export function markValidatorAutoSubmitEmail(email: string): void {
    try {
        if (email) localStorage.setItem(VALIDATOR_AUTOSUBMIT_EMAIL_KEY, email);
    } catch {
        /* ignore - worst case the email gate shows as normal */
    }
}

/** Read-once: clears the flag so it doesn't leak into later, unrelated use. */
export function consumeValidatorAutoSubmitEmail(): string | null {
    try {
        const value = localStorage.getItem(VALIDATOR_AUTOSUBMIT_EMAIL_KEY);
        localStorage.removeItem(VALIDATOR_AUTOSUBMIT_EMAIL_KEY);
        return value;
    } catch {
        return null;
    }
}
